import type { ReactNode } from "react";
import Loading from "ui/components/Loading";
import HomePlanDetailsSection from "./HomePlanDetailsSection";
import IncludeSubLocationsCheckbox from "./IncludeSubLocationsCheckbox";
import type { LocationSummaryScope } from "./locationSummaryTypes";
import styles from "./LocationSummaryView.module.scss";
import LotBreakdownCard from "./LotBreakdownCard";
import PlansOnHomesCard from "./PlansOnHomesCard";
import SummaryHighlights from "./SummaryHighlights";
import SummaryRefreshControl from "./SummaryRefreshControl";
import { useLocationContext } from "./useLocationContext";
import { useLocationSummary } from "./useLocationSummary";

export default function LocationSummaryView({ scope }: { scope: LocationSummaryScope }): ReactNode {
  const { data, loading, error, refresh, isRefreshing } = useLocationSummary(scope);
  const locationContext = useLocationContext(scope);

  if (loading || locationContext.loading) {
    return <Loading />;
  }

  if (error) {
    return <div className={styles.container}>Unable to load the location summary.</div>;
  }

  if (!data) {
    return <div className={styles.container}>No location summary data is available.</div>;
  }

  return (
    <div className={styles.container}>
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionHeaderText}>
            <h2>Home Plan Breakdown</h2>
            <p>Key home plan and customer activity across this location scope.</p>
          </div>
          <div className={styles.sectionHeaderActions}>
            {scope.kind === "location" && locationContext.leafState === "parent" && (
              <IncludeSubLocationsCheckbox scope={scope} />
            )}
            <SummaryRefreshControl aggregatedAt={data.aggregatedAt} onRefresh={refresh} isRefreshing={isRefreshing} />
          </div>
        </div>
        <SummaryHighlights
          highlights={data.highlights}
          totalHomes={data.summary.totalHomes}
          homesAssignedToLots={data.homesAssignedToLots}
          lotsPossible={locationContext.lotsPossible}
        />
        {data.summary.totalHomes > 0 && <HomePlanDetailsSection summary={data.summary} />}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>{locationContext.lotsPossible ? "Plan and Lot Overview" : "Plan Overview"}</h2>
        </div>
        <div className={locationContext.lotsPossible ? styles.overviewGrid : styles.overviewGridSingle}>
          <PlansOnHomesCard
            data={data.planBreakdownByRange}
            totalCommunityPlans={data.totalCommunityPlans}
            isLeaf={locationContext.leafState === "leaf"}
            scope={scope}
          />
          <LotBreakdownCard lotSummary={data.lotSummary} scope={scope} lotsPossible={locationContext.lotsPossible} />
        </div>
      </section>
    </div>
  );
}
